import { FeatureKey, SubscriptionStatusEnum } from '@givz/core-client';
import { ReadOnlyText } from '@ui';
import { ExtendedRadioButtonProps } from '@components/ui/radio-stack';
import { useCoreClient } from '@hooks';
import { Routes } from '@routes';
import { Link } from '@shopify/polaris';
import {
  loadingState,
  notificationErrorState,
  subscriptionsPackagesState,
  subscriptionState,
} from '@states';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { mappedGivzFeaturesSelector } from 'src/states/features';
import { freeTrialState } from 'src/states/free-trial';
import { pendingSubscriptionState } from 'src/states/subscription';
import { CampaignWithReadOnly } from 'src/types';

export const useFetchSubscription = () => {
  const [freeTrial, setFreeTrial] = useRecoilState(freeTrialState);
  const [pendingSubscription, setPendingSubscription] = useRecoilState(
    pendingSubscriptionState,
  );
  const [, setLoading] = useRecoilState(loadingState);
  const [subscription, setSubscription] = useRecoilState(subscriptionState);
  const [subscriptionsPackages, setSubscriptionsPackages] = useRecoilState(
    subscriptionsPackagesState,
  );
  const [, setNotificationError] = useRecoilState(notificationErrorState);
  const coreClient = useCoreClient();

  const fetchFreeTrial = useCallback(async () => {
    setLoading(true);
    const { data, errors } = await coreClient().FreeTrials.get({ body: {} });

    if (errors) {
      setNotificationError(errors);
    } else {
      setFreeTrial(data);
    }

    setLoading(false);
    return errors ? undefined : data;
  }, [coreClient, setFreeTrial, setLoading, setNotificationError]);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    const { data, errors } = await coreClient().Subscriptions.search();

    if (errors) {
      setNotificationError(errors);
    } else {
      const pendingSubscription = data.subscriptions.find(
        (subscription) =>
          subscription.status === SubscriptionStatusEnum.Pending,
      );
      const subscription = data.subscriptions.find(
        (subscription) =>
          subscription.status === SubscriptionStatusEnum.Active ||
          subscription.status === SubscriptionStatusEnum.Canceled,
      );

      setPendingSubscription(pendingSubscription);
      setSubscription(subscription);
    }

    setLoading(false);
    return errors ? [] : data.subscriptions;
  }, [
    coreClient,
    setLoading,
    setNotificationError,
    setPendingSubscription,
    setSubscription,
  ]);

  useEffect(() => {
    const fetchSubscriptionPackages = async () => {
      const { data, errors } =
        await coreClient().Subscriptions.Packages.search();

      if (errors) {
        setNotificationError(errors);
      } else {
        setSubscriptionsPackages(data.subscriptionsPackages);
      }
    };

    fetchFreeTrial();
    fetchSubscriptionPackages();
    fetchSubscriptions();
  }, [
    coreClient,
    fetchFreeTrial,
    fetchSubscriptions,
    setNotificationError,
    setSubscriptionsPackages,
  ]);

  return {
    currentSubscription: subscription,
    fetchSubscriptions,
    freeTrial,
    pendingSubscription,
    setCurrentSubscription: setSubscription,
    setPendingSubscription,
    subscriptionsPackages,
  };
};

export const useSubscriptionFeatures = () => {
  const router = useRouter();
  const subscription = useRecoilValue(subscriptionState);
  const features = useRecoilValue(mappedGivzFeaturesSelector);

  const featureReadOnlyText = useCallback(
    (selectedCampaign?: CampaignWithReadOnly): React.ReactNode => {
      const canceledSubscriptionText = (
        <>
          {`Free trial has ended. Please `}
          <Link
            onClick={() =>
              router.push({
                pathname: Routes.account.subscription,
              })
            }
          >
            choose a subscription
          </Link>
        </>
      );

      const featureUpsellText = (
        <>
          {`To use this feature, you must first `}
          <Link
            onClick={() =>
              router.push({
                pathname: Routes.account.subscription,
              })
            }
          >
            upgrade your subscription
          </Link>
        </>
      );

      const readOnlyCampaignText = 'Archived incentives cannot be updated';

      return subscription?.status === SubscriptionStatusEnum.Canceled
        ? canceledSubscriptionText
        : selectedCampaign?.readOnly
        ? readOnlyCampaignText
        : featureUpsellText;
    },
    [router, subscription?.status],
  );

  const isFeatureActive = useCallback(
    (feature: FeatureKey): boolean => {
      if (!features || !subscription) {
        return false;
      } else if (subscription.features.includes(feature)) {
        return true;
      } else if (!features[feature] || !features[feature].upsell) {
        return false;
      }
      return true;
    },
    [features, subscription],
  );

  const isFeatureUpsell = useCallback(
    (feature: FeatureKey): boolean => {
      return (
        isFeatureActive(feature) &&
        !subscription!.features.includes(feature) &&
        features![feature].upsell
      );
    },
    [features, isFeatureActive, subscription],
  );

  const isFeatureReadOnly = useCallback(
    (feature: FeatureKey, selectedCampaign?: CampaignWithReadOnly): boolean => {
      return (
        selectedCampaign?.readOnly ||
        isFeatureUpsell(feature) ||
        !subscription ||
        subscription.status !== SubscriptionStatusEnum.Active
      );
    },
    [isFeatureUpsell, subscription],
  );

  const toFeatureButtons = useCallback(
    (params: {
      buttons: ExtendedRadioButtonProps[];
      modifyHelpText?: boolean;
      selectedCampaign?: CampaignWithReadOnly;
    }): ExtendedRadioButtonProps[] => {
      const { buttons, modifyHelpText, selectedCampaign } = params;
      return buttons
        .filter(({ feature }) => !feature || isFeatureActive(feature))
        .map((button) => {
          const disabled = button.feature
            ? isFeatureReadOnly(button.feature, selectedCampaign)
            : false;
          const helpText =
            button.feature &&
            !selectedCampaign?.readOnly &&
            isFeatureUpsell(button.feature) ? (
              <ReadOnlyText text={featureReadOnlyText(selectedCampaign)} />
            ) : (
              button.helpText
            );
          return {
            ...button,
            disabled,
            ...(modifyHelpText && { helpText }),
          };
        });
    },
    [featureReadOnlyText, isFeatureActive, isFeatureReadOnly, isFeatureUpsell],
  );

  return {
    isFeatureActive,
    isFeatureReadOnly,
    readOnlyHelpText: featureReadOnlyText,
    toFeatureButtons,
  };
};
